import { useEffect } from 'react';
import { useUserData } from './useUserData';
import { toggleScreenProtection } from '@/lib/screenProtection';

/**
 * Hook to automatically manage screenshot protection based on user role
 * 
 * Rules:
 * - Admins (isAdmin: true) can take screenshots
 * - Artists (isArtist: true) can take screenshots
 * - All other users have screenshot protection enabled
 * 
 * Implementation:
 * - iOS: Uses custom native plugin (ScreenProtectionPlugin) for role-based protection
 * - Android: Uses Capacitor Privacy Screen plugin for role-based protection
 * - Web: No-op (screenshots cannot be prevented on web platforms)
 */
export function useScreenProtection() {
  const { userData, loading } = useUserData();

  useEffect(() => {
    // Wait until user data is loaded
    if (loading) {
      console.log('[ScreenProtection] 🔄 Loading user data...');
      return;
    }

    // If no user is logged in, enable protection by default
    if (!userData) {
      console.log('[ScreenProtection] ❌ No user logged in, enabling protection');
      toggleScreenProtection(true);
      return;
    }

    // Check if user is admin or artist
    const isAdmin = userData.isAdmin === true;
    const isArtist = userData.isArtist === true;
    const canTakeScreenshots = isAdmin || isArtist;

    console.log('[ScreenProtection] 📱 User role check:', {
      uid: userData.uid,
      isAdmin,
      isArtist,
      canTakeScreenshots
    });

    // Log user permission status
    if (canTakeScreenshots) {
      const reason = isAdmin ? 'Admin' : 'Artist';
      console.log(`[ScreenProtection] ✅ ALLOWED to take screenshots (${reason})`);
    } else {
      console.log('[ScreenProtection] 🚫 BLOCKED - Screenshots disabled for this user');
    }

    // Enable protection if user is NOT admin or artist
    // (i.e., disable protection if user IS admin or artist)
    toggleScreenProtection(!canTakeScreenshots);

  }, [userData, loading]);

  return {
    isProtected: !userData?.isAdmin && !userData?.isArtist
  };
}
